/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { IMachine } from '@umati/dashboard-types';
import Fuse from 'fuse.js';
import { IFilterData } from 'redux/filterDataType';
import { ISpecification } from 'Specifications';
import { Filter, FilterFair, FilterFavourite, FilterHalls, FilterSpec, SimpleFilter } from 'utilities/Filter';

export interface IFilterMachineData extends IFilterData {
  onlineOnly: boolean;
  Specification: ISpecification | 'all';
  ParentId: string;
}

const searchKeys = [
  'Data.ComponentName.text',
  'Data.DeviceClass',
  'Data.Manufacturer.text',
  'Data.Model.text',
  'Data.ProductInstanceUri',
  'MachineId',
  'ShortId',
  'TypeDefinition'
];
const fuseOptions = { keys: searchKeys, includeScore: true, threshold: 0.4 };
export const FilterMachineFuzzySearch = new FilterSpec<IMachine, IFilterMachineData>(
  'FuzzySearch',
  (machines, searchValue?: string) => {
    const fuse = new Fuse(machines, fuseOptions);
    if (typeof searchValue !== 'string' || searchValue === '') {
      return machines;
    }
    return fuse.search(searchValue).map(m => m.item);
  },
  'searchValue'
);

export const FilterMachineOnline = new SimpleFilter<IMachine, IFilterMachineData>(
  'Online',
  (m: IMachine, isOnline?: boolean) => {
    if (!isOnline) {
      return true;
    }
    return !!m.Online;
  },
  'onlineOnly'
);

export const FilterMachineSpecification = new SimpleFilter<IMachine, IFilterMachineData>(
  'Specification',
  (m: IMachine, specification?: 'all' | ISpecification) => {
    if (!specification || specification === 'all') {
      return true;
    }
    return specification.TypeDefinitions.filter(e => e.Type === m.TypeDefinition).length === 1;
  },
  'Specification'
);

export const FilterMachineParent = new SimpleFilter<IMachine, IFilterMachineData>(
  'ParentId',
  (m: IMachine, parentId?: 'all' | string) => {
    if (!parentId || parentId === 'all' || parentId === '') {
      return true;
    }
    return m.ParentId === parentId;
  },
  'ParentId'
);

export const FilterMachineFair = FilterFair<IMachine, IFilterMachineData>();
export const FilterMachineHalls = FilterHalls<IMachine, IFilterMachineData>();
export const FilterMachineFavourite = FilterFavourite<IMachine, IFilterMachineData>();

const MachineFilters : FilterSpec<IMachine, IFilterMachineData>[] = [
  FilterMachineFair,
  FilterMachineHalls,
  FilterMachineOnline,
  FilterMachineFavourite,
  FilterMachineFuzzySearch,
  FilterMachineSpecification,
  FilterMachineParent
];

export class MachinesFilter extends Filter<IMachine, IFilterMachineData> {
  constructor (filterData: IFilterMachineData) {
    super(filterData, MachineFilters);
  }
}
